import { useNode } from "@craftjs/core";
import React from "react";
import {
  Tabs,
  MarginInput,
  PaddingInput,
  UnitInput,
  ColorInput,
  Panel,
  IconInput,
  AlignInput,
  TabsV2,
  RangeNumberInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";

export const IconSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    icon,
    customIcon,
    align,
    hasCustomIcon,
    size,
    sizeMobile,
    sizeTablet,
    color,
    padding,
    margin,
    hoverColor,
    hoverPadding,
    hoverMargin,
    transition,
  } = props;

  function GeneralSettings() {
    const colorTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverSettings(),
      },
    ];
    function normalSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            value={color}
            onChange={(val) => {
              setProp((props) => {
                props.color = val;
              });
            }}
            label={"Color"}
          />
        </>
      );
    }
    function hoverSettings() {
      return (
        <>
          {" "}
          <ColorInput
            resetValue={undefined}
            value={hoverColor}
            onChange={(val) => {
              setProp((props) => {
                props.hoverColor = val;
              });
            }}
            label={"Color"}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("color", "background-color")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["color", "background-color"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }

    return (
      <div className="g-settings__wrapper">
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: icon },
            { key: hasCustomIcon },
            { key: customIcon },
            { key: sizeMobile },
            { key: sizeTablet },
            { key: size },
            { key: align },
            { key: color },
            { key: hoverColor },
          ])}
          id={`block-setting-${id}-icon`}
          title={"Icon"}
          initialOpen={true}
        >
          <IconInput
            resetValue={undefined}
            activeIcon={icon}
            setActiveIcon={(val) => {
              setProp((props) => {
                props.icon = val;
              });
            }}
            supportCustom
            hasCustomIcon={hasCustomIcon}
            setHasCustomIcon={(val) => {
              setProp((props) => {
                props.hasCustomIcon = val;
              });
            }}
            customIcon={customIcon}
            setCustomIcon={(val) => {
              setProp((props) => {
                props.customIcon = val;
              });
            }}
          />
          <UnitInput
            isResponsive={true}
            valueMobile={sizeMobile}
            valueTablet={sizeTablet}
            resetValue={undefined}
            resetValueMobile={undefined}
            resetValueTablet={undefined}
            value={size}
            onChange={(val) => {
              setProp((props) => {
                props.size = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.sizeMobile = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.sizeTablet = val;
              });
            }}
            label={"Size"}
          />

          <AlignInput
            resetValue={undefined}
            value={align}
            onChange={(val) => {
              setProp((props) => {
                props.align = val;
              });
            }}
            label={"Align"}
          />
          <TabsV2 items={colorTabs} />
        </Panel>
      </div>
    );
  }
  function AdvancedSettings() {
    const layoutTabs = [
      {
        label: "Normal",
        key: "normal",
        children: normalLayoutSettings(),
      },
      {
        label: "Hover",
        key: "hover",
        children: hoverLayoutSettings(),
      },
    ];
    function normalLayoutSettings() {
      return (
        <>
          {" "}
          <MarginInput
            resetValue={undefined}
            value={margin}
            onChange={(val) => {
              setProp((props) => {
                props.margin = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            label={"Padding"}
            value={padding}
            onChange={(val) => {
              setProp((props) => {
                props.padding = val;
              });
            }}
          />
        </>
      );
    }
    function hoverLayoutSettings() {
      return (
        <>
          {" "}
          <MarginInput
            resetValue={undefined}
            value={hoverMargin}
            onChange={(val) => {
              setProp((props) => {
                props.hoverMargin = val;
              });
            }}
            label={"Margin"}
          />
          <PaddingInput
            resetValue={undefined}
            label={"Padding"}
            value={hoverPadding}
            onChange={(val) => {
              setProp((props) => {
                props.hoverPadding = val;
              });
            }}
          />
          <RangeNumberInput
            min={0}
            max={10}
            step={0.05}
            value={transition?.types?.[("margin", "padding")]?.duration}
            onChange={(val) => {
              changeTransition(
                ["margin", "padding"],
                { duration: val },
                transition,
                setProp,
              );
            }}
            label={"Transition Time"}
          />
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: padding },
            { key: margin },
            { key: hoverPadding },
            { key: hoverMargin },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <TabsV2 items={layoutTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: icon },
        { key: hasCustomIcon },
        { key: customIcon },
        { key: sizeMobile },
        { key: sizeTablet },
        { key: size },
        { key: align },
        { key: color },
        { key: hoverColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: padding },
        { key: margin },
        { key: hoverPadding },
        { key: hoverMargin },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        id={`block-settings-tab-${id}`}
        type="block-setting"
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
